import { useLayoutEffect, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import './DevSiteBanner.css'

// The one canonical production host. Anything else (staging, Vercel preview
// URLs, custom domains) is treated as a non-production environment.
const PROD_HOST   = 'pitstop.back40e.com'
const PROD_ORIGIN = 'https://pitstop.back40e.com'

// Local development hosts, where a "go to production" nudge would just be noise.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', ''])

/**
 * Shown at the top of every page when the site is being served from anywhere
 * other than the production host — e.g. the staging site (pitstop-dev) or a
 * preview deployment. Points users to the real thing, preserving their current
 * path so an in-progress link (like an invite) carries over.
 *
 * The banner sits ABOVE the fixed navbar rather than overlaying it: its
 * measured height is published as --app-banner-height on <html>, which the
 * navbar's `top` and every full-viewport calc(100vh - 56px) page consume.
 * Measured (not hardcoded) because the text wraps on narrow screens.
 */
export default function DevSiteBanner() {
  const { pathname, search } = useLocation()
  const bannerRef = useRef<HTMLDivElement>(null)

  const host = window.location.hostname
  const isProduction = host === PROD_HOST || LOCAL_HOSTS.has(host)

  useLayoutEffect(() => {
    const el = bannerRef.current
    if (isProduction || !el) return

    const publishHeight = () =>
      document.documentElement.style.setProperty('--app-banner-height', `${el.offsetHeight}px`)

    publishHeight()
    // Re-publish when the banner wraps/unwraps on viewport resize.
    // (Guarded: ResizeObserver doesn't exist in the Jest/jsdom environment.)
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(publishHeight)
      : null
    observer?.observe(el)

    return () => {
      observer?.disconnect()
      document.documentElement.style.removeProperty('--app-banner-height')
    }
  }, [isProduction])

  if (isProduction) return null

  const prodUrl = `${PROD_ORIGIN}${pathname}${search}`

  return (
    <div className="dev-site-banner" role="alert" ref={bannerRef}>
      <AlertTriangle size={16} className="dev-site-banner__icon" />
      <span className="dev-site-banner__text">
        You're on the Pitstop development site. For the live site, head to{' '}
        <a className="dev-site-banner__link" href={prodUrl}>
          pitstop.back40e.com
        </a>
      </span>
    </div>
  )
}
